import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { ThemeProvider } from './contexts/ThemeContext';
import Home from './pages/landing/Home';
import Login from './pages/auth/Login';
import Register from './pages/auth/Register';
import ForgotPassword from './pages/auth/ForgotPassword';
import ResetPassword from './pages/auth/ResetPassword';
import Profile from './pages/profile/Profile';
import Mentorias from './pages/mentorias/Mentorias';
import Empresas from './pages/empresas/Empresas';
import ScrollTo from './pages/landing/ScrollTo';
import PruebaMatrix from './pages/PruebaMatrix';
import DataAdventure from './pages/datadventure/DataAdventure';
import AdminLayout from './layouts/AdminLayout';
import AdminDashboard from './pages/admin/AdminDashboard';
import UserManagement from './pages/admin/UserManagement';
import LeadsCRM from './pages/admin/LeadsCRM';
import CohortManagement from './pages/admin/CohortManagement';
import ServiceReceipts from './pages/admin/ServiceReceipts';
import AdminLinkedIn from './pages/admin/AdminLinkedIn';
import AdminProgress from './pages/admin/AdminProgress';
import LandingV2 from './pages/funnel/LandingV2';
import Courses from './pages/cursos/Courses';
import CourseDetail from './pages/cursos/CourseDetail';
import LessonPlayground from './pages/cursos/LessonPlayground';
import PresentacionProyecto from './pages/cursos/PresentacionProyecto';
import { verifyToken, type User, hasCursos, hasBootcamp } from './services/authService';
import Bootcamps from './pages/bootcamps/Bootcamps';
import LinkedInMetrics from './pages/linkedin/LinkedInMetrics';
import AccessGate from './components/AccessGate';
import './styles/App.css';

const stripeKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
const stripePromise = stripeKey ? loadStripe(stripeKey) : null;
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || 'PENDIENTE_CLIENT_ID';

function App() {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const userData = await verifyToken();
        if (userData) {
          setUser(userData);
        }
      } catch (error) {
        console.error('Auth check failed:', error);
      } finally {
        setLoading(false);
      }
    };
    checkAuth();

    // Re-verify every 60s so access changes propagate without hard refresh
    const interval = setInterval(async () => {
      try {
        const fresh = await verifyToken();
        if (fresh) {
          setUser(prev => {
            if (!prev) return fresh;
            if (prev.has_access !== fresh.has_access || prev.is_admin !== fresh.is_admin) {
              return fresh;
            }
            return prev;
          });
        }
      } catch { /* silent */ }
    }, 60_000);

    return () => clearInterval(interval);
  }, []);

  const handleLoginSuccess = (userData: User) => {
    setUser(userData);
  };

  const handleLogout = () => {
    setUser(null);
    localStorage.removeItem('auth_token');
    window.location.href = '/';
  };

  const updateSubscription = (subscribed: boolean) => {
    // Para compatibilidad con código anterior
    console.log('Update subscription:', subscribed);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Cargando...</p>
        </div>
      </div>
    );
  }

  return (
    <ThemeProvider>
      <HelmetProvider>
        <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
          <Elements stripe={stripePromise}>
            <Router>
              <div className="App">
                <Routes>
                <Route path="/" element={<Home user={user} onUpdateSubscription={updateSubscription} onLogout={handleLogout} />} />

              {/* Auth routes */}
              <Route
                path="/login"
                element={
                  user ? (
                    <Navigate to="/cursos" replace />
                  ) : (
                    <Login onLoginSuccess={handleLoginSuccess} />
                  )
                }
              />
              <Route
                path="/register"
                element={
                  user ? (
                    <Navigate to="/cursos" replace />
                  ) : (
                    <Register onRegisterSuccess={handleLoginSuccess} />
                  )
                }
              />
              <Route
                path="/forgot-password"
                element={user ? <Navigate to="/cursos" replace /> : <ForgotPassword />}
              />
              <Route
                path="/reset-password"
                element={user ? <Navigate to="/cursos" replace /> : <ResetPassword />}
              />

              {/* Legacy /members redirect */}
              <Route path="/members" element={<Navigate to="/cursos" replace />} />

              {/* Profile routes */}
              <Route
                path="/profile"
                element={user ? <Profile user={user} /> : <Navigate to="/login" replace />}
              />
              <Route
                path="/profile/change-password"
                element={user ? <Profile user={user} /> : <Navigate to="/login" replace />}
              />

              {/* Redirect old /auth route to /login */}
              <Route path="/auth" element={<Navigate to="/login" replace />} />
              
              {/* Mentorías page */}
              <Route path="/mentorias" element={<Mentorias />} />
              
              {/* Empresas page */}
              <Route path="/empresas" element={<Empresas />} />
              
              {/* Prueba Matrix */}
              <Route path="/pruebaMatrix" element={<PruebaMatrix />} />
              
              {/* Cursos (acceso flag 1 = cursos, o flag 2 = bootcamp para contenido compartido) */}
              <Route path="/cursos" element={
                !user ? <Navigate to="/login" replace /> :
                (hasCursos(user) || hasBootcamp(user) || user.is_admin) ? <Courses /> :
                <AccessGate type="cursos" userName={user.first_name} />
              } />
              <Route path="/cursos/presentacion-proyectos/guia" element={
                !user ? <Navigate to="/login" replace /> :
                (hasCursos(user) || hasBootcamp(user) || user.is_admin) ? <PresentacionProyecto /> :
                <AccessGate type="cursos" userName={user.first_name} />
              } />
              <Route path="/cursos/:id" element={
                !user ? <Navigate to="/login" replace /> :
                (hasCursos(user) || hasBootcamp(user) || user.is_admin) ? <CourseDetail /> :
                <AccessGate type="cursos" userName={user.first_name} />
              } />
              <Route path="/cursos/:courseId/playground" element={
                !user ? <Navigate to="/login" replace /> :
                (hasCursos(user) || hasBootcamp(user) || user.is_admin) ? <LessonPlayground user={user} /> :
                <AccessGate type="cursos" userName={user.first_name} />
              } />

              {/* Bootcamps (acceso flag 2 = bootcamp) */}
              <Route path="/bootcamps" element={
                !user ? <Navigate to="/login" replace /> :
                (hasBootcamp(user) || user.is_admin) ? <Bootcamps /> :
                <AccessGate type="bootcamp" userName={user.first_name} />
              } />
              {/* Cohort-aware routes: /bootcamps/:id/:cohortCode */}
              <Route path="/bootcamps/:id/:cohortCode" element={
                !user ? <Navigate to="/login" replace /> :
                (hasBootcamp(user) || user.is_admin) ? <CourseDetail /> :
                <AccessGate type="bootcamp" userName={user.first_name} />
              } />
              <Route path="/bootcamps/:courseId/:cohortCode/playground" element={
                !user ? <Navigate to="/login" replace /> :
                (hasBootcamp(user) || user.is_admin) ? <LessonPlayground user={user} /> :
                <AccessGate type="bootcamp" userName={user.first_name} />
              } />
              {/* Legacy routes sin cohortCode — redirect o fallback */}
              <Route path="/bootcamps/:id" element={
                !user ? <Navigate to="/login" replace /> :
                (hasBootcamp(user) || user.is_admin) ? <CourseDetail /> :
                <AccessGate type="bootcamp" userName={user.first_name} />
              } />
              <Route path="/bootcamps/:courseId/playground" element={
                !user ? <Navigate to="/login" replace /> :
                (hasBootcamp(user) || user.is_admin) ? <LessonPlayground user={user} /> :
                <AccessGate type="bootcamp" userName={user.first_name} />
              } />

              {/* Funnel de calificación (ex /bootcamp) */}
              <Route path="/Inicia" element={<LandingV2 />} />
              <Route path="/bootcamp" element={<Navigate to="/Inicia" replace />} />
              <Route path="/v2" element={<Navigate to="/Inicia" replace />} />

              {/* Data Adventure - juego educativo */}
              <Route path="/datadventure" element={<DataAdventure />} />

              {/* Mi LinkedIn - métricas (cualquier user logueado) */}
              <Route path="/mi-linkedin" element={
                !user ? <Navigate to="/login" replace /> : <LinkedInMetrics />
              } />

              {/* Admin: ver LinkedIn de un alumno específico */}
              <Route path="/admin/linkedin/:userId" element={
                user?.is_admin ? <LinkedInMetrics /> : <Navigate to="/" replace />
              } />

              {/* Admin - Panel unificado con sidebar */}
              <Route
                path="/admin"
                element={
                  user?.is_admin ? (
                    <AdminLayout />
                  ) : (
                    <Navigate to="/" replace />
                  )
                }
              >
                <Route index element={<AdminDashboard />} />
                <Route path="users" element={<UserManagement />} />
                <Route path="crm" element={<LeadsCRM />} />
                <Route path="cohorts" element={<CohortManagement />} />
                <Route path="receipts" element={<ServiceReceipts />} />
                <Route path="linkedin" element={<AdminLinkedIn />} />
                <Route path="progress" element={<AdminProgress />} />
              </Route>

              {/* Scroll to section route */}
              <Route path="/scrollto/:section" element={<ScrollTo />} />
                </Routes>
              </div>
            </Router>
          </Elements>
        </GoogleOAuthProvider>
      </HelmetProvider>
    </ThemeProvider>
  );
}

export default App;